import { useCallback, useEffect, useMemo, useState } from 'react';
import inspectionStorage from '../services/inspectionStorage';
import apiService from '../services/apiService';
import {
  CHRONO_OFFLINE_MSG,
  formatRapportDisplayLabel,
  saveRapportMetaFromApiRapport,
} from '../utils/rapportDisplay';

/**
 * Méta rapport serveur (id, numero, statut) stockée localement après sync / validation.
 */
export function useRapportMeta(missionId) {
  const [meta, setMeta] = useState(null);

  const reload = useCallback(async () => {
    const m = await inspectionStorage.get(`mission_${missionId}_rapport_meta`);
    setMeta(m || null);
  }, [missionId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const rapportTitle = useMemo(
    () => formatRapportDisplayLabel({ meta, missionId }),
    [meta, missionId],
  );

  const applySyncResult = useCallback(
    async (result) => {
      if (result?.success && result.data?.rapport?.id) {
        await saveRapportMetaFromApiRapport(
          inspectionStorage,
          missionId,
          result.data.rapport,
        );
        await reload();
      }
    },
    [missionId, reload],
  );

  const validateRapport = useCallback(async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      alert(CHRONO_OFFLINE_MSG);
      return { success: false };
    }
    let m = meta;
    if (!m?.id) {
      m = await inspectionStorage.get(`mission_${missionId}_rapport_meta`);
    }
    if (!m?.id) {
      alert(
        'Synchronisez d\'abord l\'inspection pour créer le rapport sur le serveur, puis validez-le.',
      );
      return { success: false };
    }
    if (m.statut === 'valide' && m.numero) {
      alert('Ce rapport est déjà validé.');
      return { success: true, alreadyValid: true };
    }
    const res = await apiService.validateRapport(m.id);
    if (!res.success) {
      const isChrono =
        res.status === 503 ||
        (res.error && String(res.error).toLowerCase().includes('chrono'));
      alert(isChrono ? CHRONO_OFFLINE_MSG : (res.error || 'Erreur lors de la validation'));
      return res;
    }
    const d = res.data || {};
    await inspectionStorage.set(`mission_${missionId}_rapport_meta`, {
      id: d.id || m.id,
      numero: d.numero_rapport ?? d.numero ?? null,
      statut: d.statut ?? 'valide',
    });
    await reload();
    return { success: true };
  }, [meta, missionId, reload]);

  const showValidateButton = !!meta?.id && !(meta.statut === 'valide' && meta.numero);

  return {
    meta,
    rapportTitle,
    reloadMeta: reload,
    applySyncResult,
    validateRapport,
    showValidateButton,
  };
}
